import { useCallback, useEffect, useMemo } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { add, endOfMonth } from 'date-fns';
import { atomWithDefault } from 'jotai/utils';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { KpiPeriod } from '../../../../data-models/company-financials.data-model';
import { companyStateJ } from '../../../../services/state/CompanyStateJ';
import { monthsInPeriod } from '../../Performance/components/DateSettings/DateSettings';
import { companyMetricsAtomJ } from '../../../../services/state/CompanyMetricsStateJ';
import { allCompanyFinancialsState } from '../../state/CompanyFinancialsState';
import { financialsDateViewByCompanyAtom } from '../../state/FinancialsDateViewPreferences';
import {
  computeMaxFuturePeriods,
  computeMaxPastPeriods,
  defaultRangeForFrequency,
  endOfPreviousFiscalPeriod,
  financialsDateRangeLabel,
  isMaxMode as checkIsMaxMode,
  MAX_RANGE,
  PastPeriodRangeOptions,
} from './financeDatePickerUtils';
import { useAuditorDateConstraints } from './useAuditorDateConstraints';
import { trackEvent } from '../../../../util/datadogTracking';

export const selectedFrequencyFinancialsState = atom<KpiPeriod | null>(null);

export const selectedFinancialsRangeState = atom<number | null>(null);

// FIXME: this should be a derived atom based on range - refactor and move logic from useSyncRangeAndEndDate to this atom
export const selectedEndDateFinancialsState = atom<Date | null>(null);

export const pastRangeState = atomWithDefault<number | null>((get) => {
  const frequency = get(selectedFrequencyFinancialsState);
  return frequency ? defaultRangeForFrequency(frequency) : null;
});
export const futureRangeState = atom<number | null>(null);

export function useFinancialsDateSettings() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const [selectedFrequency, setSelectedFrequency] = useAtom(selectedFrequencyFinancialsState);
  const [pastRange, setPastRange] = useAtom(pastRangeState);
  const [futureRange, setFutureRange] = useAtom(futureRangeState);
  const auditorConstraints = useAuditorDateConstraints();
  const setDateViewPref = useSetAtom(financialsDateViewByCompanyAtom(companyId));

  const isMaxMode = checkIsMaxMode(pastRange, futureRange);

  useSyncRangeAndEndDate(pastRange, futureRange);
  const endDate = useAtomValue(selectedEndDateFinancialsState);

  const handleMaxToggle = useCallback(() => {
    if (auditorConstraints) {
      return;
    }
    setPastRange(MAX_RANGE);
    setFutureRange(MAX_RANGE);
    if (selectedFrequency) {
      setDateViewPref({ frequency: selectedFrequency, pastRange: MAX_RANGE, futureRange: MAX_RANGE });
    }
  }, [auditorConstraints, selectedFrequency, setPastRange, setFutureRange, setDateViewPref]);

  const handleFrequencyChange = useCallback(
    (frequency: KpiPeriod) => {
      if (auditorConstraints) {
        return;
      }
      setSelectedFrequency(frequency);
      // Finance period type changed (FD-3121).
      trackEvent('finance.period_changed', { period_type: frequency });
      setPastRange(MAX_RANGE);
      setFutureRange(MAX_RANGE);
      setDateViewPref({ frequency, pastRange: MAX_RANGE, futureRange: MAX_RANGE });
    },
    [auditorConstraints, setFutureRange, setPastRange, setSelectedFrequency, setDateViewPref]
  );

  const handleSelectRange = useCallback(
    (selectedRange: number, future: boolean) => {
      if (auditorConstraints) {
        return;
      }

      if (future) {
        const concretePastDefault = PastPeriodRangeOptions[selectedFrequency!][0];
        if (isMaxMode) {
          setPastRange(concretePastDefault);
        }
        if (futureRange === selectedRange && !isMaxMode) {
          if (pastRange != null) {
            setFutureRange(null);
            if (selectedFrequency) {
              setDateViewPref({ frequency: selectedFrequency, pastRange, futureRange: null });
            }
          }
        } else {
          setFutureRange(selectedRange);
          if (selectedFrequency) {
            const newPastRange = isMaxMode ? concretePastDefault : (pastRange ?? concretePastDefault);
            setDateViewPref({
              frequency: selectedFrequency,
              pastRange: newPastRange,
              futureRange: selectedRange,
            });
          }
        }
      } else {
        if (isMaxMode) {
          setFutureRange(null);
        }
        if (pastRange === selectedRange && !isMaxMode) {
          if (futureRange != null) {
            setPastRange(null);
          }
        } else {
          setPastRange(selectedRange);
          if (selectedFrequency) {
            const newFutureRange = isMaxMode ? null : (futureRange ?? null);
            setDateViewPref({
              frequency: selectedFrequency,
              pastRange: selectedRange,
              futureRange: newFutureRange,
            });
          }
        }
      }
    },
    [
      auditorConstraints,
      isMaxMode,
      futureRange,
      pastRange,
      selectedFrequency,
      setFutureRange,
      setPastRange,
      setDateViewPref,
    ]
  );

  const label = auditorConstraints
    ? `${auditorConstraints.startDate} to ${auditorConstraints.endDate}`
    : financialsDateRangeLabel(selectedFrequency!, pastRange, futureRange);

  return {
    label,
    selectedFrequency,
    pastRange,
    futureRange,
    isMaxMode,
    handleSelectRange,
    handleFrequencyChange,
    handleMaxToggle,
    endDate,
    auditorConstraints,
  };
}

function useSyncRangeAndEndDate(pastRange: number | null, futureRange: number | null) {
  const auditorConstraints = useAuditorDateConstraints();
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const fye = useAtomValue(companyStateJ(companyId))?.fye ?? 12;
  const selectedFrequency = useAtomValue(selectedFrequencyFinancialsState)!;
  const setRange = useSetAtom(selectedFinancialsRangeState);
  const setEndDate = useSetAtom(selectedEndDateFinancialsState);
  const financials = useAtomValue(allCompanyFinancialsState(companyId));

  const defaultEndDate = useMemo(() => {
    return endOfPreviousFiscalPeriod(fye - 1, selectedFrequency);
  }, [fye, selectedFrequency]);

  useEffect(() => {
    let resolvedPast = pastRange ?? 0;
    if (resolvedPast === MAX_RANGE) {
      resolvedPast = computeMaxPastPeriods(financials, selectedFrequency, defaultEndDate);
    }
    let resolvedFuture = futureRange ?? 0;
    if (resolvedFuture === MAX_RANGE) {
      resolvedFuture = computeMaxFuturePeriods(financials, selectedFrequency, defaultEndDate);
    }
    setRange(resolvedPast + resolvedFuture);
  }, [pastRange, futureRange, setRange, financials, selectedFrequency, defaultEndDate]);

  useEffect(() => {
    if (auditorConstraints) {
      return;
    }

    if (futureRange === null) {
      setEndDate(defaultEndDate);
    } else if (futureRange === MAX_RANGE) {
      const maxFuture = computeMaxFuturePeriods(financials, selectedFrequency, defaultEndDate);
      if (maxFuture === 0) {
        setEndDate(defaultEndDate);
      } else {
        setEndDate(
          endOfMonth(
            add(defaultEndDate, {
              months: monthsInPeriod[selectedFrequency] * maxFuture,
            })
          )
        );
      }
    } else {
      setEndDate(
        endOfMonth(
          add(defaultEndDate, {
            months: monthsInPeriod[selectedFrequency] * futureRange,
          })
        )
      );
    }
  }, [auditorConstraints, defaultEndDate, setEndDate, futureRange, pastRange, selectedFrequency, financials]);
}

export function useMostRecentReportDate() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const mostRecentReportDate = useAtomValue(companyMetricsAtomJ({ companyId }))?.metrics?.at(
    0
  )?.kpiLastReportedDateActual;

  return { company, mostRecentReportDate };
}
